// path: src/Cosmetics.js
// Browse unlocked cosmetics and equip / unequip titles, frames, badges, avatars.

import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import AvatarFrameDecor from "./Components/cosmetics/AvatarFrameDecor";
import NameBadgeChip from "./Components/cosmetics/NameBadgeChip";
import CollectionSortControl from "./Components/CollectionSortControl";
import {
    COSMETIC_TYPES,
    getAvatarFrameClassName,
    getCosmeticBrowserSections,
    getCosmeticSourceLabel,
    getEquippedCosmetics,
    isSpecialCollectionAvatar,
    normalizeCosmeticsState,
} from "./utils/cosmetics";
import { loadCollectionSortKey, sortAvatarEntries } from "./utils/collectionSort";

const TYPE_TABS = [
    { id: COSMETIC_TYPES.AVATAR, label: "Avatars" },
    { id: COSMETIC_TYPES.AVATAR_FRAME, label: "Frames" },
    { id: COSMETIC_TYPES.TITLE, label: "Titles" },
    { id: COSMETIC_TYPES.NAME_BADGE, label: "Badges" },
];

const PUBLIC = process.env.PUBLIC_URL || "";
const SCROLL_TOP_SHOW_PX = 220;

const Cosmetics = ({
    cosmetics = null,
    unlockedCardIds = [],
    onEquipCosmetic,
    onUnequipCosmeticType,
    previewImageSrc = null,
    previewImageAlt = "Profile preview",
}) => {
    const [activeType, setActiveType] = useState(COSMETIC_TYPES.AVATAR);
    const [sortKey, setSortKey] = useState(() => loadCollectionSortKey());
    const [showScrollTop, setShowScrollTop] = useState(false);
    const cosmeticsState = useMemo(
        () => normalizeCosmeticsState(cosmetics),
        [cosmetics]
    );
    const equipped = useMemo(
        () => getEquippedCosmetics(cosmeticsState),
        [cosmeticsState]
    );
    const sections = useMemo(
        () =>
            getCosmeticBrowserSections(cosmeticsState, {
                unlockedCardIds,
            }),
        [cosmeticsState, unlockedCardIds]
    );
    const activeSection =
        sections.find((section) => section.type === activeType) || sections[0];
    const avatarGroups = useMemo(() => {
        const items = activeSection?.items || [];
        if (activeType !== COSMETIC_TYPES.AVATAR) {
            return { base: [], special: [] };
        }

        const base = [];
        const special = [];
        items.forEach((entry) => {
            if (!entry.unlocked) {
                return;
            }
            if (isSpecialCollectionAvatar(entry.item)) {
                special.push(entry);
            } else {
                base.push(entry);
            }
        });
        return { base, special };
    }, [activeSection, activeType]);

    const sortedAvatarGroups = useMemo(() => {
        return {
            base: sortAvatarEntries(avatarGroups.base, sortKey),
            special: sortAvatarEntries(avatarGroups.special, sortKey),
        };
    }, [avatarGroups, sortKey]);

    const frameClassName = getAvatarFrameClassName(equipped.avatarFrame);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [activeType]);

    useEffect(() => {
        const updateScrollTopVisibility = () => {
            const y =
                window.pageYOffset ||
                document.documentElement.scrollTop ||
                document.body.scrollTop ||
                0;
            setShowScrollTop(y > SCROLL_TOP_SHOW_PX);
        };

        updateScrollTopVisibility();
        window.addEventListener("scroll", updateScrollTopVisibility, {
            passive: true,
        });
        return () => {
            window.removeEventListener("scroll", updateScrollTopVisibility);
        };
    }, []);

    const scrollToTop = () => {
        const reduceMotion =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({
            top: 0,
            behavior: reduceMotion ? "auto" : "smooth",
        });
    };

    const handleAvatarClick = (entry) => {
        if (!entry || !entry.unlocked) {
            return;
        }
        if (entry.equipped) {
            if (typeof onUnequipCosmeticType === "function") {
                onUnequipCosmeticType(entry.item.type);
            }
            return;
        }
        if (typeof onEquipCosmetic === "function") {
            onEquipCosmetic(entry.item.id);
        }
    };

    const renderAvatarButton = (entry) => {
        const { item, unlocked, equipped: isEquipped } = entry;
        const locked = !unlocked;

        return (
            <button
                key={item.id}
                type="button"
                className={`cosmeticsAvatarButton${
                    isEquipped ? " cosmeticsAvatarButton--equipped" : ""
                }${locked ? " cosmeticsAvatarButton--locked" : ""}`}
                onClick={() => handleAvatarClick(entry)}
                disabled={locked}
                aria-label={
                    locked
                        ? `${item.name} locked`
                        : isEquipped
                        ? `${item.name} equipped — click to unequip`
                        : `Equip ${item.name} as profile picture`
                }
                aria-pressed={isEquipped}
                title={
                    locked
                        ? "Unlock this card in your Collection"
                        : isEquipped
                        ? "Equipped"
                        : item.name
                }
            >
                {item.imageSrc ? (
                    <img
                        src={item.imageSrc}
                        alt=""
                        className={`cosmeticsAvatarImage${
                            locked
                                ? " cosmeticsAvatarImage--locked"
                                : " cosmeticsAvatarImage--unlocked"
                        }`}
                        draggable={false}
                        loading="lazy"
                    />
                ) : (
                    <span className="cosmeticItem__avatarMark">◉</span>
                )}
            </button>
        );
    };

    const renderAvatarSection = (headingId, title, entries) => {
        return (
            <section
                className="collectionSection cosmeticsAvatarSection"
                aria-labelledby={headingId}
            >
                <div className="collectionSectionHeader">
                    <h2
                        id={headingId}
                        className="impactFont collectionSectionTitle"
                    >
                        {title}
                    </h2>
                    <div className="collectionSectionProgress">
                        {entries.length}
                    </div>
                </div>

                <div
                    className="cosmeticsAvatarGrid"
                    role="list"
                    aria-label={title}
                >
                    {entries.map((entry) => (
                        <div key={entry.item.id} role="listitem">
                            {renderAvatarButton(entry)}
                        </div>
                    ))}
                </div>
            </section>
        );
    };

    return (
        <div className="cosmeticsScreen">
            <div
                className="cosmeticsPreviewPanel cosmeticsPreviewPanel--sticky"
                aria-label="Equipped preview"
            >
                <h1 className="impactFont pageTitle cosmeticsTitle">Cosmetics</h1>
                <div
                    className={[
                        "profileImageFrame",
                        frameClassName,
                        !equipped.avatarFrame ? "profileImageFrame--bare" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                    <div className="profileImageFrame__media">
                        {previewImageSrc ? (
                            <img
                                src={previewImageSrc}
                                alt={previewImageAlt}
                                className="profileImage"
                            />
                        ) : (
                            <div className="profileImagePlaceholder">
                                Unlock a Collection card to choose an avatar.
                            </div>
                        )}
                    </div>
                    <AvatarFrameDecor
                        visualKey={equipped.avatarFrame?.visualKey}
                    />
                </div>

                <div className="cosmeticsPreviewMeta">
                    <div className="cosmeticsPreviewMeta__row">
                        <span className="cosmeticsPreviewMeta__label">Avatar</span>
                        <span className="cosmeticsPreviewMeta__value">
                            {equipped.avatar?.name || "None"}
                        </span>
                    </div>
                    <div className="cosmeticsPreviewMeta__row">
                        <span className="cosmeticsPreviewMeta__label">Title</span>
                        <span className="cosmeticsPreviewMeta__value">
                            {equipped.title?.name || "None"}
                        </span>
                    </div>
                    <div className="cosmeticsPreviewMeta__row">
                        <span className="cosmeticsPreviewMeta__label">Frame</span>
                        <span className="cosmeticsPreviewMeta__value">
                            {equipped.avatarFrame?.name || "Default"}
                        </span>
                    </div>
                    <div className="cosmeticsPreviewMeta__row">
                        <span className="cosmeticsPreviewMeta__label">Badge</span>
                        <span className="cosmeticsPreviewMeta__value cosmeticsPreviewMeta__value--badge">
                            {equipped.nameBadge ? (
                                <>
                                    <NameBadgeChip badge={equipped.nameBadge} size="sm" />
                                    <span>{equipped.nameBadge.name}</span>
                                </>
                            ) : (
                                "None"
                            )}
                        </span>
                    </div>
                </div>

                <div
                    className="cosmeticsTabs cosmeticsTabs--sticky"
                    role="tablist"
                    aria-label="Cosmetic types"
                >
                    {TYPE_TABS.map((tab) => {
                        const section = sections.find(
                            (item) => item.type === tab.id
                        );
                        const count = section
                            ? section.items.filter((entry) => entry.unlocked)
                                  .length
                            : 0;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeType === tab.id}
                                className={`cosmeticsTab${
                                    activeType === tab.id
                                        ? " cosmeticsTab--active"
                                        : ""
                                }`}
                                onClick={() => {
                                    setActiveType(tab.id);
                                }}
                            >
                                {tab.label}
                                <span className="cosmeticsTab__count">
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeType === COSMETIC_TYPES.AVATAR ? (
                <div className="cosmeticsAvatarBrowser">
                    <CollectionSortControl
                        id="cosmetics-avatar-sort"
                        value={sortKey}
                        onChange={setSortKey}
                    />

                    {renderAvatarSection(
                        "cosmetics-avatar-base-heading",
                        "Base Corgis",
                        sortedAvatarGroups.base
                    )}
                    {renderAvatarSection(
                        "cosmetics-avatar-special-heading",
                        "Special Corgis",
                        sortedAvatarGroups.special
                    )}

                    {sortedAvatarGroups.base.length === 0 &&
                        sortedAvatarGroups.special.length === 0 && (
                            <div className="cosmeticsEmpty">
                                No avatars yet. Unlock cards in your Collection to
                                use them here.
                            </div>
                        )}
                </div>
            ) : activeType === COSMETIC_TYPES.MAP_BADGE ? (
                <div className="cosmeticsEmpty">Coming Soon...</div>
            ) : (
                <div
                    className="cosmeticsList"
                    role="list"
                    aria-label={activeSection?.label}
                >
                    {(activeSection?.items || []).map((entry) => {
                        const {
                            item,
                            unlocked,
                            equipped: isEquipped,
                            comingSoon,
                        } = entry;
                        const locked = !unlocked;
                        const isFrame = item.type === COSMETIC_TYPES.AVATAR_FRAME;
                        const isTitle = item.type === COSMETIC_TYPES.TITLE;
                        const isNameBadge = item.type === COSMETIC_TYPES.NAME_BADGE;
                        const frameAssetSrc = item.assetSrc
                            ? `${PUBLIC}${item.assetSrc}`
                            : null;

                        return (
                            <div
                                key={item.id}
                                className={`cosmeticItem${
                                    isFrame ? " cosmeticItem--frame" : ""
                                }${isTitle ? " cosmeticItem--title" : ""}${
                                    isNameBadge ? " cosmeticItem--nameBadge" : ""
                                }${
                                    isEquipped ? " cosmeticItem--equipped" : ""
                                }${locked ? " cosmeticItem--locked" : ""}${
                                    comingSoon ? " cosmeticItem--comingSoon" : ""
                                }`}
                                role="listitem"
                            >
                                {isFrame ? (
                                    <div
                                        className="cosmeticItem__framePreviewWrap"
                                    >
                                        {frameAssetSrc ? (
                                            <img
                                                src={frameAssetSrc}
                                                alt=""
                                                className="cosmeticItem__frameAsset"
                                                draggable={false}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <AvatarFrameDecor
                                                visualKey={item.visualKey}
                                            />
                                        )}
                                        {unlocked ? (
                                            <button
                                                type="button"
                                                className={`profileSmallButton cosmeticItem__frameEquip${
                                                    isEquipped
                                                        ? " profileSmallButton--secondary"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    isEquipped
                                                        ? onUnequipCosmeticType(
                                                              item.type
                                                          )
                                                        : onEquipCosmetic(
                                                              item.id
                                                          )
                                                }
                                            >
                                                {isEquipped
                                                    ? "Unequip"
                                                    : "Equip"}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}

                                {isNameBadge ? (
                                    <div
                                        className="cosmeticItem__badgePreviewWrap"
                                        aria-hidden="true"
                                    >
                                        <NameBadgeChip badge={item} size="lg" />
                                    </div>
                                ) : null}

                                <div className="cosmeticItem__content">
                                    <div className="cosmeticItem__top">
                                        <div className="cosmeticItem__name">
                                            {comingSoon && locked
                                                ? "?????"
                                                : item.name}
                                        </div>
                                        <div className="cosmeticItem__status">
                                            {isEquipped
                                                ? "Equipped"
                                                : unlocked
                                                ? "Unlocked"
                                                : comingSoon
                                                ? "Coming soon"
                                                : "Locked"}
                                        </div>
                                    </div>
                                    {!isFrame && (
                                        <div className="cosmeticItem__description">
                                            {comingSoon && locked
                                                ? "Reserved reward — details arrive later."
                                                : item.description}
                                        </div>
                                    )}
                                    <div className="cosmeticItem__source">
                                        {getCosmeticSourceLabel(item)}
                                    </div>
                                </div>

                                {!isFrame ? (
                                <div className="cosmeticItem__actions">
                                    {unlocked && !isEquipped && (
                                        <button
                                            type="button"
                                            className="profileSmallButton"
                                            onClick={() =>
                                                onEquipCosmetic(item.id)
                                            }
                                        >
                                            Equip
                                        </button>
                                    )}
                                    {unlocked && isEquipped && (
                                        <button
                                            type="button"
                                            className="profileSmallButton profileSmallButton--secondary"
                                            onClick={() =>
                                                onUnequipCosmeticType(item.type)
                                            }
                                        >
                                            Unequip
                                        </button>
                                    )}
                                </div>
                                ) : null}
                            </div>
                        );
                    })}

                    {(activeSection?.items || []).length === 0 && (
                        <div className="cosmeticsEmpty">
                            {`No ${
                                activeSection?.label?.toLowerCase() || "cosmetics"
                            } yet. Clear Story stages to earn rewards.`}
                        </div>
                    )}
                </div>
            )}

            {showScrollTop ? (
                <button
                    type="button"
                    className="cosmeticsScrollTop"
                    onClick={scrollToTop}
                    aria-label="Back to top"
                    title="Back to top"
                >
                    <svg
                        className="cosmeticsScrollTop__icon"
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            fill="currentColor"
                            d="M12 5.5l-7 7 1.4 1.4L11 9.3V19h2V9.3l4.6 4.6L19 12.5l-7-7z"
                        />
                    </svg>
                </button>
            ) : null}
        </div>
    );
};

export default Cosmetics;
